import { useState } from "react";
import { Lock, Delete, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PinEntry() {
  const { unlockCarer, logout } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (value) => {
    if (value.length < 4) { setError("PIN is at least 4 digits"); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/pin/verify", { pin: value });
      unlockCarer();
    } catch (e) {
      setError(apiError(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const press = (d) => {
    if (busy || pin.length >= 8) return;
    setError("");
    setPin((p) => p + d);
  };

  const backspace = () => setPin((p) => p.slice(0, -1));

  const keyCls = "min-h-[72px] rounded-3xl border-2 border-foreground/20 bg-white text-2xl font-extrabold transition-colors duration-200 hover:bg-secondary active:bg-secondary disabled:opacity-50";

  return (
    <div data-testid="pin-entry-screen" className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl border-2 border-foreground/10 p-8 text-center">
        <span className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 grid place-items-center">
          <Lock size={32} strokeWidth={2.5} className="text-primary" />
        </span>
        <h1 className="mt-4 text-2xl font-extrabold">Carer Mode is locked</h1>
        <p className="mt-1 text-lg text-muted-foreground">Enter the carer PIN to continue.</p>

        <div data-testid="pin-dots" className="mt-6 flex justify-center gap-3 min-h-[20px]">
          {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
            <span key={i} className={`w-4 h-4 rounded-full border-2 border-foreground/30 ${i < pin.length ? "bg-primary border-primary" : "bg-white"}`} />
          ))}
        </div>

        {error && <p data-testid="pin-error" className="mt-3 text-[#C53030] font-bold">{error}</p>}

        <div className="mt-6 grid grid-cols-3 gap-3">
          {["1","2","3","4","5","6","7","8","9"].map((d) => (
            <button key={d} data-testid={`pin-key-${d}`} onClick={() => press(d)} disabled={busy} className={keyCls}>{d}</button>
          ))}
          <button data-testid="pin-key-backspace" onClick={backspace} disabled={busy} aria-label="Delete last digit" className={`${keyCls} grid place-items-center`}>
            <Delete size={28} strokeWidth={2.5} className="mx-auto" />
          </button>
          <button data-testid="pin-key-0" onClick={() => press("0")} disabled={busy} className={keyCls}>0</button>
          <button data-testid="pin-unlock-button" onClick={() => submit(pin)} disabled={busy || pin.length < 4} className="min-h-[72px] rounded-3xl bg-primary text-white text-xl font-bold transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-50">
            Go
          </button>
        </div>

        <button
          data-testid="pin-logout-button"
          onClick={async () => { await logout(); navigate("/"); }}
          className="mt-6 min-h-[48px] px-4 font-bold text-muted-foreground flex items-center gap-2 mx-auto"
        >
          <LogOut size={18} strokeWidth={2.5} /> Not you? Log out
        </button>
      </div>
    </div>
  );
}
